import { formatPrice } from "@/lib/utils";
import { BsImage } from "react-icons/bs";
import { ButtonDelete, ButtonDuplicate, ButtonEdit } from "@/components/ui/button";
import { MenuItemProps } from "./menu.props.type";
import {
  useDeleteMenuItemMutation,
  useDuplicateMenuItemMutation,
} from "@/redux/services/menu/menu.api";
import toast from "react-hot-toast";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { Trash2 } from "lucide-react";
import { Loader } from "../Spinner";
import { useRestaurantCurrency } from "@/lib/hooks";
import { toastApiError } from "@/lib/apiErrors";

const MenuItem: React.FC<MenuItemProps> = (props) => {
  const {
    sectionIndex,
    menuItemIndex,
    getValues,
    openEditModal,
    isEdit,
    appendMenuItem,
    removeMenuItem,
  } = props;

  const currency = useRestaurantCurrency();
  const confirm = useConfirm();

  const [deleteMenuItem, { isLoading: isLoadDeleteMenuItem }] =
    useDeleteMenuItemMutation();
  const [duplicateMenuItem, { isLoading: isLoadDuplicateMenuItem }] =
    useDuplicateMenuItemMutation();

  const item = getValues(
    `menuSections.${sectionIndex}.menuItems.${menuItemIndex}`
  );

  // Menu items
  const handleDuplicateMenuItem = (itemIndex: number, menuItemId?: string) => {
    if (isEdit) {
      return duplicateMenuItem(menuItemId as string);
    } else {
      const menuItem = getValues(
        `menuSections.${sectionIndex}.menuItems.${itemIndex}`
      );
      return appendMenuItem(menuItem);
    }
  };

  const handleRemoveMenuItem = async (
    menuItemIndex: number,
    menuItemId?: string
  ) => {
    if (isEdit) {
      const ok = await confirm({
        title: "Êtes-vous sûr de vouloir supprimer cet item ?",
        description: "Vous ne pourrez pas revenir en arrière !",
        confirmText: "Supprimer",
        cancelText: "Annuler",
        variant: "destructive",
        icon: <Trash2 className="h-6 w-6" />,
      });
      if (!ok) return;
      const res = await deleteMenuItem(menuItemId as string);
      if ("data" in res) {
        toast.success("Item supprimé avec succès");
      } else {
        toastApiError(
          res.error,
          "Une erreur est survenue lors de la suppression de l'item"
        );
      }
      return;
    }
    removeMenuItem(menuItemIndex);
  };

  return (
    <>
      <td className="px-4 py-2 align-middle">
        {item?.image || item?.newImageUrl ? (
          <img
            src={
              item?.newImageUrl
                ? item?.newImageUrl
                : typeof item?.image === "string"
                ? item?.image
                : item?.image?.url
            }
            alt={item?.title}
            className="w-12 h-12 object-contain"
          />
        ) : (
          <BsImage fontSize={26} />
        )}
      </td>
      <td className="px-4">{item?.title}</td>
      <td className="px-4 py-2 w-44">{formatPrice(Number(item?.basePrice), currency)}</td>
      <td className="px-4 py-2 w-32"> {item?.sortOrder}</td>
      <td className="px-4 py-2">
        <div className="space-x-2 flex items-center justify-end">
          <ButtonEdit onClick={() => openEditModal(menuItemIndex)} />

          {isLoadDuplicateMenuItem ? (
            <Loader border="border-thblue" />
          ) : (
            <ButtonDuplicate
              onClick={() => handleDuplicateMenuItem(menuItemIndex, item?.id)}
            />
          )}

          {isLoadDeleteMenuItem ? (
            <Loader border="border-danger" />
          ) : (
            <ButtonDelete
              onClick={() => handleRemoveMenuItem(menuItemIndex, item?.id)}
            />
          )}
        </div>
      </td>
    </>
  );
};

export default MenuItem;
